import { useState, useCallback, memo, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAlignLeft,
  faAlignCenter,
  faAlignRight,
  faMinus,
  faGripLines,
  faEllipsisH,
} from "@fortawesome/free-solid-svg-icons";
import { useWidgetSettings } from "../WidgetSettings";

const DividerConfigForm = memo(function DividerConfigForm({
  config,
  onUpdate,
}) {
  const { setActionButton, clearActionButton } = useWidgetSettings();
  const [formData, setFormData] = useState({
    title: config.title || "",
    show_title: config.show_title !== false,
    title_alignment: config.title_alignment || "center",
    line_style: config.line_style || "solid",
    line_color: config.line_color || "#e5e7eb",
    line_thickness: config.line_thickness || 1,
    show_line: config.show_line !== false,
    vertical_spacing: config.vertical_spacing || 16,
    horizontal_spacing: config.horizontal_spacing || 16,
    title_size: config.title_size || "medium",
    line_opacity: config.line_opacity || 100,
    show_decorative_elements: config.show_decorative_elements || false,
    decorative_style: config.decorative_style || "dots",
  });

  // Add handler to stop propagation
  const stopPropagation = useCallback((e) => {
    if (e) {
      e.stopPropagation();
      e.nativeEvent?.stopImmediatePropagation();
    }
  }, []);

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      onUpdate(formData);
    },
    [formData, onUpdate]
  );

  const handleReset = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const defaultData = {
        title: "",
        show_title: true,
        title_alignment: "center",
        line_style: "solid",
        line_color: "#e5e7eb",
        line_thickness: 1,
        show_line: true,
        vertical_spacing: 16,
        horizontal_spacing: 16,
        title_size: "medium",
        line_opacity: 100,
        show_decorative_elements: false,
        decorative_style: "dots",
      };
      setFormData(defaultData);
      onUpdate(defaultData);
    },
    [onUpdate]
  );

  const lineStyleOptions = [
    { value: "solid", label: "Solid", icon: faMinus },
    { value: "dashed", label: "Dashed", icon: faGripLines },
    { value: "dotted", label: "Dotted", icon: faEllipsisH },
  ];

  const titleSizeOptions = [
    { value: "small", label: "Small" },
    { value: "medium", label: "Medium" },
    { value: "large", label: "Large" },
    { value: "xl", label: "Extra Large" },
  ];

  const alignmentOptions = [
    { value: "left", label: "Left", icon: faAlignLeft },
    { value: "center", label: "Center", icon: faAlignCenter },
    { value: "right", label: "Right", icon: faAlignRight },
  ];

  const decorativeOptions = [
    { value: "dots", label: "Dots" },
    { value: "stars", label: "Stars" },
    { value: "diamonds", label: "Diamonds" },
    { value: "circles", label: "Circles" },
  ];

  // Set up action button
  useEffect(() => {
    const actionButton = (
      <div className="flex gap-3">
        <button
          type="button"
          onClick={handleReset}
          className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 text-white/70 rounded-lg font-medium transition-colors border border-white/20"
        >
          Reset to Default
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="flex-1 px-4 py-3 bg-primary hover:bg-primary/80 text-white rounded-lg font-medium transition-colors"
        >
          Apply Changes
        </button>
      </div>
    );

    setActionButton(actionButton);

    return () => {
      clearActionButton();
    };
  }, [setActionButton, clearActionButton, handleSubmit, handleReset]);

  return (
    <div
      className="space-y-6"
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Title Configuration */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-white font-medium">Title</Label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.show_title}
                onChange={(e) => {
                  stopPropagation(e);
                  handleChange("show_title", e.target.checked);
                }}
                className="rounded border-white/20 bg-white/5 text-primary focus:ring-primary"
              />
              <span className="text-white/70 text-sm">Show Title</span>
            </label>
          </div>

          {formData.show_title && (
            <>
              <Input
                value={formData.title}
                onChange={(e) => {
                  stopPropagation(e);
                  handleChange("title", e.target.value);
                }}
                placeholder="Enter section title"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/50"
              />

              <div className="space-y-2">
                <Label className="text-white/70 text-sm">Title Alignment</Label>
                <div className="flex gap-2">
                  {alignmentOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={(e) => {
                        stopPropagation(e);
                        handleChange("title_alignment", option.value);
                      }}
                      className={`flex-1 p-2 rounded-lg border transition-all ${
                        formData.title_alignment === option.value
                          ? "bg-primary border-primary text-white"
                          : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={option.icon}
                        className="h-4 w-4 mx-auto"
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white/70 text-sm">Title Size</Label>
                <select
                  value={formData.title_size}
                  onChange={(e) => {
                    stopPropagation(e);
                    handleChange("title_size", e.target.value);
                  }}
                  className="w-full p-2 rounded-lg bg-white/5 border-white/10 text-white"
                >
                  {titleSizeOptions.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      className="bg-black"
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>

        {/* Line Configuration */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-white font-medium">Line</Label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.show_line}
                onChange={(e) => {
                  stopPropagation(e);
                  handleChange("show_line", e.target.checked);
                }}
                className="rounded border-white/20 bg-white/5 text-primary focus:ring-primary"
              />
              <span className="text-white/70 text-sm">Show Line</span>
            </label>
          </div>

          {formData.show_line && (
            <>
              <div className="space-y-2">
                <Label className="text-white/70 text-sm">Line Style</Label>
                <div className="flex gap-2">
                  {lineStyleOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={(e) => {
                        stopPropagation(e);
                        handleChange("line_style", option.value);
                      }}
                      className={`flex-1 p-2 rounded-lg border transition-all ${
                        formData.line_style === option.value
                          ? "bg-primary border-primary text-white"
                          : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={option.icon}
                        className="h-4 w-4 mx-auto"
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/70 text-sm">Line Color</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={formData.line_color}
                      onChange={(e) => {
                        stopPropagation(e);
                        handleChange("line_color", e.target.value);
                      }}
                      className="w-12 h-8 rounded border-white/20 bg-white/5"
                    />
                    <Input
                      value={formData.line_color}
                      onChange={(e) => {
                        stopPropagation(e);
                        handleChange("line_color", e.target.value);
                      }}
                      className="flex-1 bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70 text-sm">
                    Thickness ({formData.line_thickness}px)
                  </Label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={formData.line_thickness}
                    onChange={(e) => {
                      stopPropagation(e);
                      handleChange("line_thickness", parseInt(e.target.value));
                    }}
                    className="w-full"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white/70 text-sm">
                  Line Opacity ({formData.line_opacity}%)
                </Label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={formData.line_opacity}
                  onChange={(e) => {
                    stopPropagation(e);
                    handleChange("line_opacity", parseInt(e.target.value));
                  }}
                  className="w-full"
                />
              </div>
            </>
          )}
        </div>

        {/* Spacing */}
        <div className="space-y-4">
          <Label className="text-white font-medium">Spacing</Label>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white/70 text-sm">
                Vertical Spacing ({formData.vertical_spacing}px)
              </Label>
              <input
                type="range"
                min="0"
                max="50"
                value={formData.vertical_spacing}
                onChange={(e) => {
                  stopPropagation(e);
                  handleChange("vertical_spacing", parseInt(e.target.value));
                }}
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white/70 text-sm">
                Horizontal Spacing ({formData.horizontal_spacing}px)
              </Label>
              <input
                type="range"
                min="0"
                max="50"
                value={formData.horizontal_spacing}
                onChange={(e) => {
                  stopPropagation(e);
                  handleChange("horizontal_spacing", parseInt(e.target.value));
                }}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-white font-medium">
              Decorative Elements
            </Label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.show_decorative_elements}
                onChange={(e) => {
                  stopPropagation(e);
                  handleChange("show_decorative_elements", e.target.checked);
                }}
                className="rounded border-white/20 bg-white/5 text-primary focus:ring-primary"
              />
              <span className="text-white/70 text-sm">
                Show Decorative Elements
              </span>
            </label>
          </div>

          {formData.show_decorative_elements && (
            <div className="space-y-2">
              <Label className="text-white/70 text-sm">Decorative Style</Label>
              <select
                value={formData.decorative_style}
                onChange={(e) => {
                  stopPropagation(e);
                  handleChange("decorative_style", e.target.value);
                }}
                className="w-full p-2 rounded-lg bg-white/5 border-white/10 text-white"
              >
                {decorativeOptions.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    className="bg-black"
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </form>
    </div>
  );
});

DividerConfigForm.displayName = "DividerConfigForm";

export default DividerConfigForm;
